import React, { useRef, useEffect, useState } from 'react';
import { useQueue } from '../contexts/QueueContext';
import { FaPlay, FaPause, FaForward, FaBackward, FaTimes, FaRandom, FaRedo } from 'react-icons/fa';
import styles from '../Vol1.module.css';

const QueuePlayer: React.FC = () => {
  const { queue, currentQueueIndex, isQueueActive, nextInQueue, prevInQueue, stopQueue, currentQueueSong, removeFromQueue, playQueue, shuffleQueue } = useQueue();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.5);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [repeatEnabled, setRepeatEnabled] = useState(false);
  const [shuffleEnabled, setShuffleEnabled] = useState(false);
  const prevImageUrlRef = useRef<string | null>(null);

  const song = currentQueueSong();
  useEffect(() => {
    audioRef.current = new Audio();
    audioRef.current.volume = volume;
    const audio = audioRef.current;
    const onTimeUpdate = () => setCurrentTime(audio.currentTime);
    const onLoadedMetadata = () => setDuration(audio.duration);
    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    return () => {
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.pause();
    };
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onEnded = () => {
      if (repeatEnabled) {
        audio.currentTime = 0;
        audio.play().catch(() => {});
      } else {
        const next = nextInQueue();
        if (!next) setIsPlaying(false);
      }
    };
    audio.addEventListener('ended', onEnded);
    return () => { audio.removeEventListener('ended', onEnded); };
  }, [nextInQueue, repeatEnabled]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !song) return;
    if (isQueueActive) {
      if (audio.src !== song.audioFile) {
        audio.src = song.audioFile;
        audio.currentTime = 0;
      }
      audio.play().then(() => setIsPlaying(true)).catch(() => {});
    }
  }, [isQueueActive, song?.audioFile, currentQueueIndex]);

  useEffect(() => {
    if (!isQueueActive && audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  }, [isQueueActive]);

  useEffect(() => {
    const coverUrl = song?.coverImage;
    if (prevImageUrlRef.current !== coverUrl) {
      setIsImageLoading(true);
      prevImageUrlRef.current = coverUrl || null;
    }
  }, [song?.coverImage, currentQueueIndex]);

  if (!isQueueActive || !song) return null;

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) { audio.pause(); setIsPlaying(false); }
    else { audio.play().then(() => setIsPlaying(true)).catch(() => {}); }
  };

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const t = parseFloat(e.target.value);
    if (audioRef.current) audioRef.current.currentTime = t;
    setCurrentTime(t);
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = parseFloat(e.target.value);
    setVolume(v);
    if (audioRef.current) audioRef.current.volume = v;
  };

  const handleClose = () => {
    if (audioRef.current) audioRef.current.pause();
    setIsPlaying(false);
    stopQueue();
  };

  const formatTime = (t: number) => {
    const m = Math.floor(t / 60);
    const s = '0' + Math.floor(t % 60);
    return m + ':' + s.substr(-2);
  };

  const handleShuffle = () => {
    shuffleQueue();
    setShuffleEnabled(true);
    setRepeatEnabled(false);
    setTimeout(() => setShuffleEnabled(false), 1000);
  };

  const handleRepeat = () => {
    setRepeatEnabled(!repeatEnabled);
  };

  const coverUrl = song.coverImage;
  const isVideo = coverUrl?.match(/\.(mp4|mov|webm)$/i);

  return (
    <div className={styles.container}>
      <div style={headerStyle}>
        <h1 className={styles.volumeTitle}>Queue</h1>
        <button onClick={handleClose} style={closeBtnStyle} title="Stop Queue">
          <FaTimes />
        </button>
      </div>

      <div className={styles.albumCoverContainer}>
        {isVideo ? (
          <video
            src={coverUrl}
            className={`${styles.albumCover} ${isImageLoading ? styles.loading : ''}`}
            autoPlay loop muted playsInline
            onLoadedData={() => setIsImageLoading(false)}
          />
        ) : (
          <img
            src={coverUrl}
            alt={song.title}
            className={`${styles.albumCover} ${isImageLoading ? styles.loading : ''}`}
            onLoad={() => setIsImageLoading(false)}
          />
        )}
      </div>

      <div className={styles.audioControls}>
        <button onClick={handleShuffle} className={styles.button} title="Shuffle Queue">
          <FaRandom className={shuffleEnabled ? styles.activeIcon : styles.icon} />
        </button>
        <button onClick={() => prevInQueue()} className={styles.button} title="Previous">
          <FaBackward className={styles.icon} />
        </button>
        <button onClick={togglePlay} className={`${styles.button} ${styles.playButton}`} title={isPlaying ? 'Pause' : 'Play'}>
          {isPlaying ? <FaPause className={styles.icon} /> : <FaPlay className={styles.icon} />}
        </button>
        <button onClick={() => { const next = nextInQueue(); if (!next) setIsPlaying(false); }} className={styles.button} title="Next">
          <FaForward className={styles.icon} />
        </button>
        <button onClick={handleRepeat} className={styles.button} title={repeatEnabled ? 'Disable Repeat' : 'Enable Repeat'}>
          <FaRedo className={repeatEnabled ? styles.activeIcon : styles.icon} />
        </button>
      </div>

      <div className={styles.playerControls}>
        <div className={styles.trackTitle}>
          {song.title}
          <span style={{ color: '#815353', fontSize: '0.85rem', marginLeft: '8px' }}>Vol {song.volume}</span>
        </div>
        <div className={styles.timeControls}>
          <div className={styles.timeRow}>
            <span className={styles.timeText}>{formatTime(currentTime)}</span>
            <input type="range" min="0" max={duration} value={currentTime} onChange={handleTimeChange} className={styles.timeSlider} />
            <span className={styles.timeTextRight}>{formatTime(duration)}</span>
          </div>
        </div>
        <div className={styles.volumeSliderWrapper}>
          <input type="range" min="0" max="1" step="0.01" value={volume} onChange={handleVolumeChange} className={styles.volumeSlider} />
        </div>
        <div className={styles.volumeText}>Volume</div>
      </div>

      <div className={styles.tracklist}>
        <h2 className={styles.tracklistLabel}>Queue ({currentQueueIndex + 1}/{queue.length})</h2>
        {queue.map((s, index) => {
          const isVid = s.coverImage?.match(/\.(mp4|mov|webm)$/i);
          return (
            <div key={`${s.id}-${index}`} className={styles.tracklistItem} onClick={() => playQueue(index)} style={{ cursor: 'pointer' }}>
              {isVid ? (
                <video src={s.coverImage} style={thumbStyle} autoPlay loop muted playsInline />
              ) : (
                <img src={s.coverImage} alt={s.title} style={thumbStyle} />
              )}
              <span
                style={{
                  flex: 1,
                  color: index === currentQueueIndex ? '#b08a8a' : undefined,
                }}
              >
                {s.title}
              </span>
              <span style={{ color: '#815353', fontSize: '0.8rem', marginRight: '8px' }}>Vol {s.volume}</span>
              <button
                onClick={(e) => { e.stopPropagation(); removeFromQueue(index); }}
                style={removeStyle}
                title="Remove"
              >
                <FaTimes />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const headerStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  position: 'relative', width: '100%',
};

const closeBtnStyle: React.CSSProperties = {
  position: 'absolute', right: '1rem', top: '50%', transform: 'translateY(-50%)',
  background: 'none', border: 'none', color: '#815353', cursor: 'pointer',
  fontSize: '1.2rem', padding: '8px',
};

const thumbStyle: React.CSSProperties = {
  width: '36px', height: '36px', borderRadius: '4px', objectFit: 'cover',
  marginRight: '10px', flexShrink: 0,
};

const removeStyle: React.CSSProperties = {
  background: 'none', border: 'none', color: '#815353', cursor: 'pointer',
  padding: '4px 8px', fontSize: '0.8rem',
};

export default QueuePlayer;
